import React from 'react';

import { useDispatch } from 'react-redux';

import Box from '@amzn/meridian/box';
import { type BoxProps } from '@amzn/meridian/box/box';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import _ from 'lodash';
import { deleteRestrictionListItem } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppSelector } from 'src/store/store';
import {
  isTimeRestrictionTask,
  isWeightRestrictionParentTask,
  isYesNoRestrictionTask,
} from 'src/utils/restrictionUtilsLegacy';

import IdentifyBodyLocationField from './selectors/identifyBodyLocationField';
import TaskSelector from './selectors/taskSelector';
import TimeRestrictionField from './selectors/timeRestrictionField';
import WeightRestrictionChildValueSelectors from './selectors/weightRestrictionChildValueSelectors';
import WeightRestrictionMaxRangeSelector from './selectors/weightRestrictionMaxRangeSelector';
import YesNoRestrictionField from './selectors/yesNoRestrictionField';

/**
 * Update Redux selected tasks when the array of locally selected tasks updates.
 */
const RestrictionList = () => {
  const { restrictionListItems } = useAppSelector((state) => state.newJmrPage);

  return (
    <Column spacing="300" spacingInset="300">
      {restrictionListItems.map((_, index) => (
        <RestrictionItemBox key={index} listIndex={index} />
      ))}
      <Box spacingInset="300" />
    </Column>
  );
};

/**
 * Update the "selected task name list" when the selected name changes.
 */
const RestrictionItemBox = ({ listIndex, ...otherProps }: { listIndex: number } & BoxProps) => {
  const dispatch = useDispatch();
  const { restrictionListItems, isRestrictionsPageReadOnly } = useAppSelector((state) => state.newJmrPage);
  const task = restrictionListItems[listIndex];
  const displayTimeRestrictionTask = isTimeRestrictionTask(task);
  const displayParentWeightRestrictionFields = isWeightRestrictionParentTask(task);
  const displayYesNoTask = isYesNoRestrictionTask(task);

  return (
    <Card type="outline" disabled={true} {...otherProps}>
      {/* Top Row */}
      <Row alignmentHorizontal="justify">
        {/* Index */}
        <Text type="h200" color="secondary">
          Restriction {listIndex + 1}
        </Text>

        {/* Remove Button */}
        {!isRestrictionsPageReadOnly && (
          <Button
            type="link"
            onClick={() => dispatch(deleteRestrictionListItem(listIndex))}
            data-cy={`new-jmr__remove-restriction__btn__${listIndex}`}
            data-testid={`new-jmr__remove-restriction__btn__${listIndex}`}
          >
            Remove
          </Button>
        )}
      </Row>
      <Box spacingInset="200" />

      {/* Select Task */}
      <Box spacingInset="none none 400 none">
        <Text>Task*</Text>
        <TaskSelector listIndex={listIndex} />
      </Box>

      {/* Display range selector for a "Parent Weight Restriction Task" */}
      {displayParentWeightRestrictionFields && (
        <WeightRestrictionMaxRangeSelector listIndex={listIndex} spacingInset="none none 400 none" />
      )}

      {/* "Identify Body Location" Field */}
      {!!task && <IdentifyBodyLocationField listIndex={listIndex} spacingInset="none none 400 none" />}

      {/* Handle Time Restrictions*/}
      {displayParentWeightRestrictionFields && task.weightRestrictionMaxRange > 0 ? (
        <WeightRestrictionChildValueSelectors listIndex={listIndex} />
      ) : displayTimeRestrictionTask ? (
        <TimeRestrictionField listIndex={listIndex} />
      ) : displayYesNoTask ? (
        <YesNoRestrictionField listIndex={listIndex} />
      ) : null}
    </Card>
  );
};

// **** Export default **** //

export default RestrictionList;
